import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Select, DatePicker, message } from 'antd';
import { SearchOutlined, ReloadOutlined, DatabaseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { formatIndianNumber } from '../../../utils/numberFormat';
import { getApiEndpoint, authFetch } from '../../../config/apiBase';

const { Option } = Select;

const ReportsSummary = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(0);
  const [fromDate, setFromDate] = useState(dayjs().subtract(7, 'day'));
  const [toDate, setToDate] = useState(dayjs());
  const [summaryData, setSummaryData] = useState([]);
  const [summaryTotal, setSummaryTotal] = useState(0);
  const [summaryPage, setSummaryPage] = useState(1);
  const [summaryPageSize, setSummaryPageSize] = useState(10);
  const [dataLoading, setDataLoading] = useState(false);
  const [aggregates, setAggregates] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await authFetch(getApiEndpoint('/migration/summary-fetch/users'));
      if (response.ok) {
        const result = await response.json();
        setUsers(result.data || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchSummaryData = async (page = 1, pageSize = 10) => {
    setDataLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedUser > 0) params.append('user_id', selectedUser);
      if (fromDate) params.append('from_date', fromDate.format('DD-MM-YYYY'));
      if (toDate) params.append('to_date', toDate.format('DD-MM-YYYY'));
      params.append('page', page);
      params.append('limit', pageSize);

      const response = await authFetch(getApiEndpoint(`/migration/summary-fetch/data?${params}`));
      if (response.ok) {
        const result = await response.json();
        setSummaryData(result.data || []);
        setSummaryTotal(result.total || 0);
        setSummaryPage(page);
        setSummaryPageSize(pageSize);
        setAggregates(result.aggregates || null);
      }
    } catch (error) {
      message.error('Failed to fetch summary data');
    } finally {
      setDataLoading(false);
    }
  };

  const dataColumns = [
    { title: 'User', dataIndex: 'username', key: 'username', render: (v) => v || '-' },
    { title: 'Date', dataIndex: 'date', key: 'date', render: (d) => dayjs(d).format('DD-MM-YYYY') },
    { title: 'Total SMS', dataIndex: 'totalSMS', key: 'totalSMS', render: (v) => formatIndianNumber(v), align: 'right' },
    { title: 'Sent', dataIndex: 'sent', key: 'sent', render: (v) => formatIndianNumber(v), align: 'right' },
    { title: 'Delivered', dataIndex: 'delivered', key: 'delivered', render: (v) => <span style={{color: '#52c41a'}}>{formatIndianNumber(v)}</span>, align: 'right' },
    { title: 'Failed', dataIndex: 'failed', key: 'failed', render: (v) => <span style={{color: v > 0 ? '#ff4d4f' : 'inherit'}}>{formatIndianNumber(v)}</span>, align: 'right' },
    { title: 'Pending', dataIndex: 'pending', key: 'pending', render: (v) => formatIndianNumber(v), align: 'right' },
    { title: 'In Queue', dataIndex: 'inQue', key: 'inQue', render: (v) => formatIndianNumber(v), align: 'right' },
    { title: 'Undelivered', dataIndex: 'undelivered', key: 'undelivered', render: (v) => formatIndianNumber(v), align: 'right' },
    { title: 'Blocked', dataIndex: 'block', key: 'block', render: (v) => formatIndianNumber(v), align: 'right' },
    { title: 'NDNC', dataIndex: 'NDNCDenied', key: 'NDNCDenied', render: (v) => formatIndianNumber(v), align: 'right' },
    { title: 'DR N/A', dataIndex: 'drNotAvailable', key: 'drNotAvailable', render: (v) => formatIndianNumber(v), align: 'right' },
  ];

  return (
    <div style={{ padding: '16px' }}>
      <Card 
        title={
          <span>
            <DatabaseOutlined style={{ marginRight: 8 }} />
            SMS Summary
          </span>
        }
        bodyStyle={{ padding: '16px' }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-end' }}>
            <Select
              style={{ minWidth: 180 }}
              placeholder="All Users"
              value={selectedUser}
              onChange={setSelectedUser}
              showSearch
              allowClear
              optionFilterProp="label"
            >
              <Option value={0} label="All Users">All Users</Option>
              {users.map(user => (
                <Option key={user.id} value={user.id} label={user.username}>
                  {user.username}
                </Option>
              ))}
            </Select>
            <DatePicker
              value={fromDate}
              onChange={setFromDate}
              format="DD-MM-YYYY"
              placeholder="From Date"
              style={{ minWidth: 130 }}
            />
            <DatePicker
              value={toDate}
              onChange={setToDate}
              format="DD-MM-YYYY"
              placeholder="To Date"
              style={{ minWidth: 130 }}
            />
            <Button type="primary" icon={<SearchOutlined />} onClick={() => fetchSummaryData(1, summaryPageSize)} loading={dataLoading}>
              Search
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => fetchSummaryData(summaryPage, summaryPageSize)}>
              Refresh
            </Button>
          </div>

          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table
            columns={dataColumns}
            dataSource={summaryData}
            rowKey="id"
            loading={dataLoading}
            size="small"
            scroll={{ x: 1100 }}
            pagination={{
              current: summaryPage,
              pageSize: summaryPageSize,
              total: summaryTotal,
              showSizeChanger: true,
              showQuickJumper: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${formatIndianNumber(total)} records`,
              onChange: (page, size) => fetchSummaryData(page, size),
              size: 'small',
            }}
            summary={() => {
              if (!aggregates) return null;
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row style={{ background: '#fafafa' }}>
                    <Table.Summary.Cell index={0}><strong>Overall Total ({formatIndianNumber(summaryTotal)} records)</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={1} />
                    <Table.Summary.Cell index={2} align="right"><strong>{formatIndianNumber(aggregates.totalSMS)}</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right"><strong>{formatIndianNumber(aggregates.sent)}</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right"><strong style={{color: '#52c41a'}}>{formatIndianNumber(aggregates.delivered)}</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right"><strong style={{color: aggregates.failed > 0 ? '#ff4d4f' : 'inherit'}}>{formatIndianNumber(aggregates.failed)}</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right"><strong>{formatIndianNumber(aggregates.pending)}</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={7} align="right"><strong>{formatIndianNumber(aggregates.inQue)}</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={8} align="right"><strong>{formatIndianNumber(aggregates.undelivered)}</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={9} align="right"><strong>{formatIndianNumber(aggregates.block)}</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={10} align="right"><strong>{formatIndianNumber(aggregates.NDNCDenied)}</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={11} align="right"><strong>{formatIndianNumber(aggregates.drNotAvailable)}</strong></Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default ReportsSummary;
